
import logger from "@/lib/logger";

/**
 * Enhanced and reliable function to check internet connectivity
 * Uses navigator.onLine and local origin checks to avoid CSP issues
 */
export const checkInternetConnection = async (timeout: number = 5000): Promise<boolean> => {
  // First check navigator.onLine as a quick initial check
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    logger.log("navigator.onLine reports offline");
    return false;
  }
  
  logger.log("Starting internet connectivity check");
  
  try {
    // Use a simple HEAD request to the current origin to check connectivity
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    // Get current origin for the request
    const currentOrigin = window.location.origin;
    
    // Request a static file on our own domain with a cache buster
    // This will work even if the file doesn't exist - we just need a network response
    const cacheBuster = `?nocache=${Date.now()}`;
    const url = `${currentOrigin}/connectivity-check${cacheBuster}`;
    
    logger.log(`Checking connectivity with ${url}`);
    
    const response = await fetch(url, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
      // No custom headers to avoid potential CORS issues
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    // If we reach this point, the network is up
    clearTimeout(timeoutId);
    logger.log("Connectivity check succeeded");
    return true;
  } catch (error) {
    // If the error is an abort, it means the request timed out
    if (error.name === 'AbortError') {
      logger.log("Connectivity check timed out");
      return false;
    }
    
    // For any other error, check if navigator.onLine is still true
    if (navigator.onLine) {
      logger.log("Connectivity check failed, but navigator.onLine is true");
      return true; // Still assume we're online if navigator says so
    }
    
    logger.log("Connectivity check failed and navigator.onLine is false");
    return false;
  }
};
